import React, { useState, useEffect, useMemo } from 'react';
import { X, Folder, Download, FileImage, ChevronRight } from 'lucide-react';
import clsx from 'clsx';
import { useQuery } from 'convex/react';
import { api } from '../convex/_generated/api';
import { Id } from '../convex/_generated/dataModel';

interface ViewResultsModalProps {
  jobId: string;
  isOpen: boolean;
  onClose: () => void;
}

interface ProcessedFile {
  original_name: string;
  new_name: string;
  folder: string;
  description: string;
  tags: string[];
  preview_url?: string;
}

interface JobResults {
  job_id: string;
  status: string;
  total_files: number;
  processed_files: number;
  folders_created: string[];
  files: ProcessedFile[];
  download_url: string;
}

const ViewResultsModal: React.FC<ViewResultsModalProps> = ({ jobId, isOpen, onClose }) => {
  const [selectedFolder, setSelectedFolder] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState<ProcessedFile | null>(null);

  // Fetch job and files from Convex using reactive queries
  const job = useQuery(
    api.jobs.getJobById,
    isOpen ? { jobId: jobId as Id<"jobs"> } : "skip"
  );

  const files = useQuery(
    api.files.getProcessedFilesWithUrls,
    isOpen ? { jobId: jobId as Id<"jobs"> } : "skip"
  );

  // Transform Convex data to match expected JobResults interface
  const results: JobResults | null = useMemo(() => {
    if (!job || !files) return null;

    // Extract unique folder names (categories)
    const folders = Array.from(new Set(files.map(f => f.category).filter(Boolean))) as string[];

    // Transform files to match ProcessedFile interface
    // Note: getProcessedFilesWithUrls query fetches storage URLs server-side
    // URLs are temporary (~1 hour expiration) but Convex reactive queries auto-refresh
    const processedFiles: ProcessedFile[] = files.map(file => ({
      original_name: file.originalFileName,
      new_name: file.suggestedFilename || file.originalFileName,
      folder: file.category || 'uncategorized',
      description: file.description || '',
      tags: file.tags || [],
      preview_url: file.previewUrl || undefined, // Map camelCase from Convex to snake_case
    }));

    return {
      job_id: jobId,
      status: job.status,
      total_files: job.fileCount,
      processed_files: job.processedFiles,
      folders_created: folders,
      files: processedFiles,
      download_url: '', // Download URL is constructed in handleDownload
    };
  }, [job, files, jobId]);

  // Auto-select first folder when results load
  useEffect(() => {
    if (isOpen && results && results.folders_created.length > 0 && !selectedFolder) {
      console.log('📁 ViewResultsModal: Auto-selecting folder:', results.folders_created[0]);
      setSelectedFolder(results.folders_created[0]);
    }
  }, [isOpen, results, selectedFolder]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedFolder(null);
      setSelectedImage(null);
    }
  }, [isOpen]);

  const loading = isOpen && (job === undefined || files === undefined);
  const error = null; // Convex handles errors automatically

  const handleDownload = () => {
    // Use Convex HTTP endpoint (.convex.site, not .convex.cloud)
    // .convex.cloud is for WebSocket queries/mutations
    // .convex.site is for HTTP routes/actions
    let convexUrl = process.env.REACT_APP_CONVEX_SITE_URL || '';

    // If not set, convert CONVEX_URL from .convex.cloud to .convex.site
    if (!convexUrl && process.env.REACT_APP_CONVEX_URL) {
      convexUrl = process.env.REACT_APP_CONVEX_URL.replace('.convex.cloud', '.convex.site');
    }

    // Final fallback: production HTTP endpoint
    if (!convexUrl) {
      convexUrl = 'https://upbeat-butterfly-447.convex.site';
    }

    const downloadUrl = `${convexUrl}/download?jobId=${jobId}`;
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = `sortingcat-${jobId}.zip`;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getFilesInFolder = (folder: string) => {
    if (!results) return [];
    return results.files.filter(file => file.folder === folder);
  };

  const getFileExtension = (filename: string) => {
    const parts = filename.split('.');
    return parts.length > 1 ? parts[parts.length - 1].toUpperCase() : 'FILE';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Backdrop */}
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={onClose}
        />
        
        {/* Modal */}
        <div className="relative z-50 w-full max-w-6xl bg-white rounded-lg shadow-xl">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900">
              Processing Results
            </h2>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                  <p className="text-gray-600">Loading results...</p>
                </div>
              </div>
            ) : error ? (
              <div className="py-12 text-center">
                <p className="text-red-600 mb-4">{error}</p>
                <button
                  onClick={handleDownload}
                  className="btn-primary"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download ZIP Instead
                </button>
              </div>
            ) : results ? (
              <div className="flex flex-col lg:grid lg:grid-cols-12 gap-4 lg:gap-6 h-auto lg:h-[500px]">
                {/* Folder List */}
                <div className="lg:col-span-2 border-b lg:border-b-0 lg:border-r border-gray-200 pb-4 lg:pb-0 lg:pr-4 overflow-y-auto">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">
                    Folders Created ({results.folders_created.length})
                  </h3>
                  <div className="flex flex-wrap gap-2 lg:flex-col lg:space-y-1 lg:gap-0">
                    {results.folders_created.map((folder) => (
                      <button
                        key={folder}
                        onClick={() => setSelectedFolder(folder)}
                        className={clsx(
                          'text-left px-3 py-2 rounded-lg transition-colors flex items-center',
                          'w-auto lg:w-full',
                          selectedFolder === folder
                            ? 'bg-blue-50 text-blue-700'
                            : 'hover:bg-gray-50 text-gray-700 bg-gray-50 lg:bg-transparent'
                        )}
                      >
                        <Folder className="w-4 h-4 mr-2 flex-shrink-0" />
                        <span className="text-sm truncate">{folder}</span>
                        <span className="ml-2 lg:ml-auto text-xs text-gray-500">
                          {getFilesInFolder(folder).length}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* File List */}
                <div className="lg:col-span-4 overflow-y-auto border-b lg:border-b-0 border-gray-200 pb-4 lg:pb-0">
                  {selectedFolder ? (
                    <div>
                      <h3 className="text-sm font-semibold text-gray-700 mb-3">
                        Files in &ldquo;{selectedFolder}&rdquo;
                      </h3>
                      <div className="space-y-2">
                        {getFilesInFolder(selectedFolder).map((file, index) => (
                          <div
                            key={index}
                            className={clsx(
                              'border rounded-lg p-3 cursor-pointer transition-all',
                              selectedImage === file
                                ? 'border-blue-400 bg-blue-50'
                                : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                            )}
                            onClick={() => setSelectedImage(file)}
                          >
                            <div className="flex items-center">
                              {/* File Icon */}
                              <div className="flex-shrink-0 w-10 h-10 bg-purple-100 rounded flex items-center justify-center mr-3">
                                <FileImage className="w-5 h-5 text-purple-600" />
                              </div>

                              {/* File Info */}
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 truncate">
                                  {file.new_name}
                                </p>
                                <p className="text-xs text-gray-500 truncate">
                                  Original: {file.original_name}
                                </p>
                                <div className="flex items-center mt-1">
                                  <span className="text-xs font-medium text-purple-600 bg-purple-100 px-2 py-0.5 rounded">
                                    {getFileExtension(file.original_name)}
                                  </span>
                                  {file.tags && file.tags.length > 0 && (
                                    <span className="ml-2 text-xs text-gray-400">
                                      {file.tags.length} tag{file.tags.length > 1 ? 's' : ''}
                                    </span>
                                  )}
                                </div>
                              </div>

                              {/* Chevron */}
                              <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0 ml-2" />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-32 lg:h-full text-gray-500">
                      Select a folder to view files
                    </div>
                  )}
                </div>

                {/* File Details - Full width on mobile, stacked above file list visually */}
                <div className="lg:col-span-6 lg:border-l border-gray-200 lg:pl-4 overflow-y-auto order-first lg:order-last">
                  {selectedImage ? (
                    <div className="bg-gray-50 lg:bg-transparent rounded-lg p-4 lg:p-0">
                      <h3 className="text-sm font-semibold text-gray-700 mb-3">
                        File Details
                      </h3>

                      {/* File Name Display */}
                      <div className="mb-4">
                        <p className="text-sm font-medium text-gray-900 break-words">{selectedImage.original_name}</p>
                      </div>

                      <div className="space-y-3">
                        <div>
                          <p className="text-xs font-medium text-gray-500">New Name</p>
                          <p className="text-sm text-gray-900 break-words">{selectedImage.new_name}</p>
                        </div>

                        <div>
                          <p className="text-xs font-medium text-gray-500">Original Name</p>
                          <p className="text-sm text-gray-900 break-words">{selectedImage.original_name}</p>
                        </div>

                        <div>
                          <p className="text-xs font-medium text-gray-500 mb-1">Description</p>
                          <p className="text-sm text-gray-700 leading-relaxed break-words whitespace-pre-wrap">
                            {selectedImage.description}
                          </p>
                        </div>

                        <div>
                          <p className="text-xs font-medium text-gray-500 mb-1">Tags</p>
                          <div className="flex flex-wrap gap-1.5">
                            {selectedImage.tags.map((tag, i) => (
                              <span
                                key={i}
                                className="px-2 py-1 bg-gray-100 text-xs rounded-full text-gray-700"
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-24 lg:h-full text-gray-500 text-sm bg-gray-50 lg:bg-transparent rounded-lg">
                      Select an image to view details
                    </div>
                  )}
                </div>
              </div>
            ) : null}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between p-6 border-t border-gray-200">
            <div className="text-sm text-gray-600">
              {results && (
                <span>
                  {results.processed_files} files processed into {results.folders_created.length} folders
                </span>
              )}
            </div>
            <div className="flex space-x-3">
              <button
                onClick={handleDownload}
                className="btn-primary flex items-center"
              >
                <Download className="w-4 h-4 mr-2" />
                Download ZIP
              </button>
              <button
                onClick={onClose}
                className="btn-secondary"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewResultsModal;